<style scoped>

  .ui.segment {
    min-height: 230px;
  }

</style>

<template>
  
  <div>

    <div class="ui large breadcrumb">
      <a class="clickable" @click="$historyBack()">
        <i class="reply icon m-r-5"></i> Voltar
      </a>
      <i class="right chevron icon divider"></i>
      <div class="bold section">
        Financeiro do Laudo
      </div>      
    </div>

    <div class="ui divider"></div>

    <div v-if="loading"><i class="spinner circle loading blue icon"></i> Carregando ...</div>   

    <div v-else class="animated fadeIn">

      <router-link class="ui button m-b-20" :to="`/procs/certs/${cert.id}/show?historyBack=true`">
        <i class="search plus blue icon"></i> Laudo
      </router-link>

      <router-link class="ui button m-b-20 m-l-10" :to="`/entries/customers/${cert.customer.id}?historyBack=true`">
        <i class="edit icon"></i> Clínica
      </router-link>

      <p v-if="cert.customerInvoiceId" class="grey text">
        <i class="info circle icon"></i> Os dados financeiros desse laudo não podem ser alterados porque o laudo está associando a um fechamento
      </p>

      <div class="ui grid">      

        <div class="eight wide computer sixteen wide tablet sixteen wide mobile column">
          <div ref="certDataSegment" class="ui segment">
            <h4 class="ui dividing header">
              Dados do Laudo
              <span v-if="finalityLabel" class="ui label">{{ finalityLabel }}</span>
              <span v-if="cert.customerInvoiceId" class="ui teal label clickable" @click="onShowCustomerInvoice()">
                Fechamento <div class="detail">{{ cert.customerInvoiceId }}</div>
              </span>
            </h4>    
            <div class="ui form">           
              <div class="fields">
                <div class="two wide field">
                  <label class="form-label">Número</label>
                  {{ cert.id }}
                </div>
                <div class="three wide field">
                  <label class="form-label">Data</label>
                  {{ $formatDate(cert.date) }}
                </div>
                <div class="four wide field">
                  <label class="form-label">Status</label>
                  <cert-status :status="cert.status" />
                </div>                                        
                <div class="seven wide field">
                  <label class="form-label">Serviço</label>
                  {{ cert.service.description }}                
                </div>
              </div>          
              <div class="fields">
                <div class="five wide field">
                  <label class="form-label">Tipo do Serviço</label>
                  <service-type-icon :icon="cert.serviceType.icon" />
                  {{ cert.serviceType.description }}
                </div>
                <div class="four wide field">
                  <label class="form-label">Valor do Serviço</label>
                  {{ formatValue(serviceValue) }}
                </div>
                <div class="seven wide field">
                  <label class="form-label">Valor em Dinheiro</label>
                  {{ formatValue(moneyValue) }}
                </div>                
              </div>
              <div class="fields">
                <div class="five wide field">
                  <label class="form-label">Valor do Laudo</label>
                  {{ formatValue(certValue) }}
                </div>
                <div class="eleven wide field">
                  <label class="form-label">Colaborador</label>
                  {{ cert.user.name.toUpperCase() + ' ' + cert.user.lastName.toUpperCase() }}
                </div>                             
              </div>          
              
            </div>      
          </div>
        </div> 

        <div class="eight wide computer sixteen wide tablet sixteen wide mobile column">
          <div ref="customerSegment" class="ui segment">
            <h4 class="ui dividing header">
              Clínica
            </h4>          
            <div class="ui form">
              <div class="fields">
                <div class="five wide field">
                  <label class="form-label">Pessoa</label>              
                  <span v-if="cert.customer.person == 'F'"><i class="user outline blue icon"></i> Física</span>
                  <span v-else><i class="building outline red icon"></i> Jurídica</span>
                </div>
                <div class="eleven wide field">
                  <label class="form-label">Nome</label>
                  {{ cert.customer.shortName }}
                </div>            
              </div> 
              <div class="fields">
                <div class="five wide field">
                  <label class="form-label">Período de Pagamento</label>
                  {{ paymentPeriod.description }}
                </div>
                <div class="eleven wide field">
                  <label class="form-label">Forma de Pagamento</label>
                  <i :class="paymentType.icon"></i>
                  {{ paymentType.description }}
                </div>      
              </div>
              <div class="fields">
                <div class="five wide field">
                  <label class="form-label">Pagamento para</label>
                  {{ paymentToLabel }}
                </div>
                <div class="eleven wide field">
                  <label class="form-label">Emite NF</label>
                  {{ issuesInvoiceLabel }}
                </div>      
              </div>
            </div>
          </div>
        </div>      

        <div class="sixteen wide computer sixteen wide tablet sixteen wide mobile column">
          <div class="ui segment">
            <h4 class="ui dividing header">Animal</h4>        
            <button class="ui mini basic button m-b-20" @click="$refs.animalPicturesModal.show(cert.animal.id)">
              <i class="camera violet icon"></i> Fotos
            </button>     
            <router-link v-show="cert.animalId" class="ui mini basic button m-l-10" :to="`/entries/animal-registrations/${cert.animalRegistrationId}?historyBack=true`">
              <i class="search teal icon"></i> Visualizar Ficha
            </router-link>                
            <div class="ui form">
              <div class="fields">
                <div class="three wide field">
                  <label class="form-label">Clínica</label>
                  {{ cert.customer.shortName }}
                </div>
                <div class="two wide field">
                  <label class="form-label">Ficha Cadastral</label>
                  {{ cert.animalRegistration.registrationId }}
                </div>                  
                <div class="three wide field">
                  <label class="form-label">Nome do Animal</label>
                  {{ cert.animal.name }}
                  <i v-if="cert.animal.dead" class="cross grey icon m-l-5"></i>
                </div>
                <div class="two wide field">
                  <label class="form-label">Sexo</label>
                  <span v-if="cert.animal.sex == 'male'"><i class="mars blue icon"></i> Macho</span>
                  <span v-else><i class="venus pink icon"></i> Fêmea</span>
                </div>              
                <div class="three wide field">
                  <label class="form-label">Raça</label>
                  {{ cert.animal.race.name }}
                </div>
                <div class="three wide field">
                  <label class="form-label">Espécie</label>
                  {{ cert.animal.race.species.name }}
                </div>
              </div>
              <div class="fields">
                <div v-if="cert.animal.birthDate" class="three wide field">
                  <label class="form-label">Idade</label>
                  {{ animalAge }}
                </div>
                <div v-else class="three wide field">
                  <label class="form-label">Faixa Etária</label>
                  {{ animalAge }}
                </div>
                <div class="two wide field">
                  <label class="form-label">Peso</label>
                  {{ cert.animal.weight ? $formatCurrency(cert.animal.weight) + " KG" : "Não cadastrado" }}
                </div> 
                <div class="three wide field">
                  <label class="form-label">Tutor</label>
                  {{ cert.animal.owner.name }}
                </div>
                <div class="two wide field">
                  <label class="form-label">Telefone</label>
                  {{ cert.animal.owner.phone || "Não cadastrado" }}
                </div>
                <div class="three wide field">
                  <label class="form-label">Celular</label>
                  {{ cert.animal.owner.mobile || "Não cadastrado" }}
                </div>
                <div class="three wide field">
                  <label class="form-label">E-mail</label>
                  {{ cert.animal.owner.email || "Não cadastrado" }}
                </div>
              </div>            
            </div>    
          </div>
        </div> 

        <div class="sixteen wide column">

          <div class="ui segment">

            <h4 class="ui dividing header">
              Recebimento
            </h4>

            <button v-if="certValue && canUpdate" class="ui mini basic button" @click="$refs.receivableModal.create()">
              <i class="plus blue icon"></i> Adicionar
            </button>

            <table v-if="hasReceivables" class="ui celled selectable tablet stackable table animated fadeIn">
              <thead v-if="$isDesktop">
                <tr>
                  <th>Parcela</th>
                  <th>Valor</th>
                  <th>Forma de Pagamento</th>
                  <th>Desconto Simavet</th>
                  <th>Desconto Colaborador</th>
                  <th>Valor Recebido</th>
                  <th v-if="canUpdate"></th>
                </tr>  
              </thead>
              <tbody>
                <tr v-if="!receivable.removed" v-for="(receivable, index) in receivables" @click="onReceivableClick(index)">
                  <td>
                    <strong class="mobile-only">Parcela: </strong> {{ receivable.sequence }}                  
                  </td>
                  <td>
                    <strong class="mobile-only">Valor: </strong> R$ {{ $formatCurrency(receivable.value) }}   
                  </td>
                  <td :class="{ negative: (receivable.paymentType == 'waiting_transfer' || receivable.paymentType == 'manual_input') }">
                    <strong class="mobile-only">Forma de Pagamento: </strong> 
                    <i :class="receivable.paymentTypeData.icon"></i>
                    {{ receivable.paymentTypeData.description }}
                  </td>              
                  <td>
                    <strong class="mobile-only">Desconto Simavet: </strong> R$ {{ $formatCurrency(receivable.companyDiscount) }}
                  </td>
                  <td>
                    <strong class="mobile-only">Desconto Colaborador: </strong> R$ {{ $formatCurrency(receivable.employeeDiscount) }}
                  </td>
                  <td>
                    <strong class="mobile-only">Valor Recebido: </strong> R$ {{ $formatCurrency(receivable.receivedValue) }}
                  </td>              
                  <td v-if="canUpdate" style="width: 50px;">
                    <button class="ui small icon button" @click="onRemoveReceivable($event, index)">
                      <i class="times icon"></i>
                    </button>
                  </td>
                </tr>                
              </tbody>
            </table>

            <p v-else class="grey text m-t-20 animated fadeIn">
              <i class="info circle icon"></i>
              {{ certValue || cert.customerInvoiceId ? 'Nenhuma parcela adicionada' : 'Valor do serviço não definido' }}
            </p>

          </div>

        </div>

        <div class="sixteen wide column">
          <button class="ui basic button" @click="$historyBack()">
            <i class="reply icon"></i> Cancelar
          </button>   
          <button v-if="isUpdating && canUpdate" class="ui basic red button m-l-10" :class="{ loading: removing }" 
            @click="$refs.confirmRemovalModal.show()">
            <i class="trash icon"></i> Excluir
          </button>     
          <button v-if="certValue && canUpdate" class="ui right floated blue button" :class="{ loading: saving, disabled: hasManualInputPaymentType }" 
            @click="onConfirm()">
            <i class="check icon"></i>
            {{ isCreating ? 'Confirmar' : 'Atualizar' }}
          </button>         
        </div>

      </div>  

    </div>  

    <animal-pictures-modal ref="animalPicturesModal" />

    <receivable-form-modal ref="receivableModal" :cert="cert" :certValue="certValue" :moneyValue="moneyValue" 
      :receivables="receivables" @onCreate="onAddReceivable" @onUpdate="onUpdateReceivable" />
    
    <yes-no-modal ref="confirmRemovalModal" danger="true" :message="`Confirma exclusão dos dados financeiros do laudo ${cert.id} ?`"  @onYes="onRemove()" />

  </div>

</template>  

<script>
  
  import CertService from "@/services/cert-service"
  import FinancialService from "@/services/financial-service"
  import AnimalService from "@/services/animal-service"
  import AnimalPicturesModal from "@/pages/entries/animals/pictures-modal"
  import ReceivableFormModal from "./receivable-form-modal"  
  import { AppBrowser, moment } from "@/utils"
  import { CertStatus, ServiceTypeIcon, YesNoModal } from "@/components"

  export default {
    
    name: "CertFinancialPage",

    data() {
      return {
        loading: true,
        saving: false,  
        removing: false,
        isCreating: false,
        isUpdating: false,
        cert: {},
        paymentPeriod: {},
        paymentType: {},
        serviceValueLabel: "",
        moneyValueLabel: "",
        paymentToLabel: "",
        issuesInvoiceLabel: "",
        selectedReceivableIndex: null,
        serviceValue: null,
        moneyValue: null,
        certValue: null,
        receivablesData: null,
        receivables: [],
        service: new CertService(),
      }        
    },

    components: {
      CertStatus,
      ServiceTypeIcon,
      AnimalPicturesModal,
      ReceivableFormModal,
      YesNoModal,
    },

    created() {
      this.loadCert()
    }, 

    computed: {

      finalityLabel() {

        let label = ""

        if (this.cert.finality == "control") {
          label = "Controle"
        } else if (this.cert.finality == "revaluation") {
          label = "Reavaliação"
        }

        return label
      },

      animalAge() {

        if (!this.cert.animal)
          return ""

        return this.cert.animal.birthDate ? moment.formatAge(this.cert.animal.birthDate) : this.getAgeRange() 
      }, 

      hasReceivables() {

        let exists = false

        for (const receivable of this.receivables) {
          if (!receivable.removed) {
            exists = true
            break
          }  
        }

        return exists
      },

      hasManualInputPaymentType() {

        for (const receivable of this.receivables) {
          if (receivable.paymentType == "manual_input")
            return true
        }

        return false
      },

      canUpdate() {
        return (this.certValue && !this.cert.customerInvoiceId)
      },
    },

    methods: {

      getAgeRange() {        
        return AnimalService.getAgeRange(this.cert.animal)
      },

      onShowCustomerInvoice() {
        AppBrowser.openNewTab(`/financial/customer-invoices/${this.cert.customerInvoiceId}/show`)
      },

      formatValue(value) {
        return value == null ? "Não definido" : "R$ " + this.$formatCurrency(value)
      },

      async loadCert() {

        this.loading = true

        try {

          this.cert = await this.service.findById(this.$route.params.id)

          if (this.cert.fdata) {

            this.isUpdating = true

          } else {
            
            this.isCreating = true

            if (!this.cert.customerInvoiceId)
              this.receivablesData = await this.service.generateCertReceivables(this.cert.id)            
          }

          this.setFinancialData()

        } catch (error) {

          this.$errorHandler(error)

        } finally {

          this.loading = false

          setTimeout(() => this.equalizeTopSegmentsHeight(), 80)
        }
      },

      equalizeTopSegmentsHeight() {
        if (this.$isDesktop)
            this.$refs.customerSegment.style.height = (this.$refs.certDataSegment.clientHeight + 1) + "px"
      },

      setFinancialData() {

        let customer = this.cert.customer
        const undefinedLabel = "Não definido"

        this.serviceValueLabel = undefinedLabel
        this.moneyValueLabel = undefinedLabel
        this.paymentPeriod.description = undefinedLabel
        this.paymentType.description = undefinedLabel
        this.paymentToLabel = undefinedLabel
        this.issuesInvoiceLabel = undefinedLabel

        this.setServiceValues()

        if (customer.paymentPeriod)        
          this.paymentPeriod = FinancialService.PAYMENT_PERIOD[customer.paymentPeriod]  
          
        if (customer.paymentType)        
          this.paymentType = FinancialService.PAYMENT_TYPE[customer.paymentType]

        if (customer.paymentTo)  
          this.paymentToLabel = customer.paymentTo == "company" ? "Simavet" : "Colaborador"

        if (typeof customer.issuesInvoice == "boolean")  
          this.issuesInvoiceLabel = customer.issuesInvoice ? "Sim" : "Não"
          
        this.certValue = this.isCreating && this.receivablesData ? this.receivablesData.certValue : this.cert.fdata.certValue

        if (!this.certValue && this.serviceValue)
          this.certValue = this.serviceValue

        if (!this.certValue)
          this.certValue = null
        
        if (this.certValue)
          this.setReceivables()
      },

      setServiceValues() {

        this.serviceValue = null
        this.moneyValue = null
        this.serviceValueLabel = ""
        this.moneyValueLabel = ""

        if (this.isCreating) {
          for (const service of this.cert.customer.services) {          
            if (service.serviceId == this.cert.serviceId && service.serviceTypeId == this.cert.serviceTypeId) {
              this.serviceValue = service.value
              if (service.moneyValue) {
                this.moneyValue = service.moneyValue
              }  
            }  
          }
        } else {
          this.serviceValue = this.cert.fdata.serviceValue
          this.moneyValue = this.cert.fdata.moneyValue
        }

        if (this.serviceValue)
          this.serviceValueLabel = "R$ " + this.$formatCurrency(this.serviceValue)

        if (this.moneyValue)
          this.moneyValueLabel = "R$ " + this.$formatCurrency(this.moneyValue)          
      },

      setReceivables() {

        if (this.cert.receivables.length > 0) {
          this.receivables = this.cert.receivables.slice()          
        } else {
          this.receivables = this.receivablesData.receivables.slice()
        }
        
        this.updateReceivables()
      },

      onReceivableClick(index) {

        if (!this.canUpdate)
          return

        this.selectedReceivableIndex = index
        
        this.$refs.receivableModal.update(this.receivables[index])
      },

      onAddReceivable(receivable) {
        this.receivables.push(receivable)
        this.updateReceivables()
      },  

      onUpdateReceivable(receivable) {

        let receivables = this.receivables.slice()

        receivables[this.selectedReceivableIndex] = receivable

        this.receivables = receivables.slice()
        
        this.updateReceivables()
      },

      onRemoveReceivable($event, index) {

        $event.stopPropagation()
        
        let receivables = this.receivables.slice()

        receivables[index].removed = true

        this.receivables = receivables

        this.updateReceivables()
      },

      updateReceivables() {

        let sequence = 0
        let receivables = this.receivables.slice()

        receivables.forEach((receivable) => {
          if (!receivable.removed) {            
            receivable.sequence = ++sequence
            receivable.paymentTypeData = FinancialService.PAYMENT_TYPE[receivable.paymentType]
          }
        })

        if (this.isMoneyValue()) {
          this.certValue = this.moneyValue
        } else {
          this.certValue = this.serviceValue
        }  

        this.receivables = receivables
      },

      isMoneyValue() {

        if (!this.moneyValue)
          return false

        const receivables = this.receivables.filter((receivable) => !receivable.removed)

        return (receivables.length == 1 && receivables[0].paymentType == "money" && receivables[0].value == this.moneyValue)
      },

      async onConfirm() {

        if (this.saving || !this.validConfirmation())
          return          

        this.saving = true
        
        try {

          await this.service.updateFinancialData(this.cert.id, this.receivables)

          this.$reloadDataTables(["daily-certs"])

          this.$msg.success("Dados atualizados com sucesso")

          this.$historyBack()

        } catch (error) {
          
          this.$errorHandler(error)
        
        } finally {

          this.saving = false
        }
      },

      validConfirmation() {

        let isOk = true
        let message = ""

        if (!this.hasReceivables) {
          message = "Nenhuma parcela adicionada"
        } else if (this.getReceivablesValue() != this.certValue) {
          message = "O valor total das parcelas é diferente do valor do laudo"
        }

        if (message) {
          isOk = false
          this.$msg.info(message)
        }

        return isOk
      },

      getReceivablesValue() {

        let value = 0

        this.receivables.forEach((receivable) => {
          if (!receivable.removed)
            value += receivable.value
        })

        return value
      },

      async onRemove() {

        this.removing = true
        
        try {

          await this.service.removeFinancialData(this.cert.id)

          this.$reloadDataTables(["daily-certs"])

          this.$msg.success("Dados removidos com sucesso")

          this.$historyBack()

        } catch (error) {
          
          this.$errorHandler(error)
        
        } finally {

          this.removing = false
        }
      },
    },
  }

</script>